import React, { useCallback, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { MANAGE_DATA_SECTION, navigateToManageDataPage, useHasFF } from 'venn-utils';
import { useHistory } from 'react-router-dom';
import type { Portfolio } from 'venn-api';
import { isNil, noop } from 'lodash';
import {
  BaseDropMenu,
  Button,
  ButtonIcon,
  GetColor,
  Icon,
  Link as LinkStyle,
  OptionIconPosition,
  SimpleMenu,
  Tooltip,
  TooltipPosition,
} from 'venn-ui-kit';
import { ConfirmationModal } from 'venn-components';
import type { DropMenuProps } from 'venn-ui-kit/src/components/drop-menu/implementations/BaseDropMenu';

enum PortfolioPolicyActions {
  SAVE = 'SAVE',
  RESET = 'RESET',
}

interface ConstraintsPolicyStatusFooterProps {
  portfolio: Portfolio | undefined;
  matchesPolicy: boolean;
  isSideBarMaximized: boolean;
  onResetToPolicyConstraints: () => void;
  onApplyToPolicy: () => void;
  onConstraintsSave?: () => void;
  onConstraintsRevert?: () => void;
  isInEditState?: boolean;
  hasConstraintsRevamp?: boolean;
  hasConstraintsError: boolean;
  /** Disables the policy sync button (Save as / Reset to Portfolio Policy) — e.g. for the benchmark
   * relative constraint, which we don't want persisted to the portfolio policy for now. */
  disablePolicySync?: boolean;
  /** True when the current constraint state is identical to the saved state, e.g. after editing and
   * then editing back to the original value. Used to disable the Revert button even while
   * isInEditState is still true. Defaults to false to preserve existing behavior for callers that
   * don't track this. */
  hasNoChanges?: boolean;
}

const OptionIcon = styled(Icon)`
  padding-left: 5px;
`;

const portfolioPolicyOptions = [
  {
    label: 'Save as Portfolio Policy',
    description: 'Save current constraints as Portfolio Policy. This will overwrite your existing policy.',
    value: PortfolioPolicyActions.SAVE,
    icon: <OptionIcon type="sign-out-alt" />,
    iconPosition: OptionIconPosition.RIGHT,
  },
  {
    label: 'Reset to Portfolio Policy',
    description:
      'Override current constraints with Portfolio Policy constraints. This will clear your current constraints.',
    value: PortfolioPolicyActions.RESET,
    icon: <OptionIcon type="sync" />,
    iconPosition: OptionIconPosition.RIGHT,
  },
];

const ConstraintsPolicyStatusFooter = ({
  portfolio,
  matchesPolicy,
  isSideBarMaximized,
  onResetToPolicyConstraints,
  onApplyToPolicy,
  onConstraintsSave = noop,
  onConstraintsRevert = noop,
  isInEditState = false,
  hasConstraintsRevamp = false,
  hasConstraintsError,
  disablePolicySync = false,
  hasNoChanges = false,
}: ConstraintsPolicyStatusFooterProps) => {
  const history = useHistory();
  const hasPortfolioLabFF = useHasFF('portfolio_lab_ff');
  const [confirmPolicyOverride, setConfirmPolicyOverride] = useState(false);

  const actionsMapper = useMemo(() => {
    return {
      [PortfolioPolicyActions.SAVE]: () => setConfirmPolicyOverride(true),
      [PortfolioPolicyActions.RESET]: () => onResetToPolicyConstraints(),
    };
  }, [setConfirmPolicyOverride, onResetToPolicyConstraints]);

  const openManageDataPage = () => {
    if (isNil(portfolio)) {
      return;
    }
    navigateToManageDataPage(
      history,
      { portfolioId: portfolio.id },
      'Portfolio Lab',
      false,
      MANAGE_DATA_SECTION.PORTFOLIO_POLICY,
    );
  };

  const triggerComponent: DropMenuProps<PortfolioPolicyActions>['triggerComponent'] = useCallback(
    (expanded, _, onToggle) => (
      <>
        <Tooltip
          isHidden={!disablePolicySync}
          content="Saving this constraint to the portfolio policy is unavailable."
          position={TooltipPosition.Right}
          usePortal
        >
          <PolicySync
            matchesPolicy={matchesPolicy}
            onClick={() => !disablePolicySync && onToggle(!expanded)}
            iconType="sync"
            className="qa-policy-sync-menu"
            disabled={disablePolicySync || isInEditState || matchesPolicy}
            isInEditState={isInEditState}
            $disabled={disablePolicySync}
            $portfolioLabFF={hasPortfolioLabFF}
          />
        </Tooltip>
        {!disablePolicySync && !isInEditState && !matchesPolicy ? (
          <StyledIcon matchesPolicy={matchesPolicy} type={expanded ? 'caret-up' : 'caret-down'} />
        ) : null}
      </>
    ),
    [matchesPolicy, isInEditState, hasPortfolioLabFF, disablePolicySync],
  );

  const menuComponent: DropMenuProps<PortfolioPolicyActions>['menuComponent'] = useCallback(
    (_, onCollapse) => (
      <StyledSimpleMenu<PortfolioPolicyActions>
        items={portfolioPolicyOptions}
        width={300}
        onChange={({ value }) => {
          actionsMapper[value]();
          onCollapse();
        }}
      />
    ),
    [actionsMapper],
  );

  if (hasConstraintsRevamp) {
    return (
      <>
        <Footer hasConstraintsRevamp={hasConstraintsRevamp} $portfolioLabFF={hasPortfolioLabFF}>
          <Container>
            <BaseDropMenu
              menuAbove
              className="drop-menu"
              filteredItems={portfolioPolicyOptions}
              triggerComponent={triggerComponent}
              menuComponent={menuComponent}
            />
          </Container>
          <FooterStatusRevamped isInEditState={isInEditState} $portfolioLabFF={hasPortfolioLabFF}>
            <StatusHeader>
              Portfolio Policy
              {isInEditState ? (
                <Icon type="minus-circle" />
              ) : (
                <MatchesPolicyIcon
                  matchesPolicy={matchesPolicy}
                  type={matchesPolicy ? 'check-circle' : 'times-circle'}
                />
              )}
            </StatusHeader>
            <span>
              Constraints {matchesPolicy ? 'match ' : 'do not match '}
              {isInEditState ? (
                <strong>portfolio policy</strong>
              ) : (
                <LinkStyle onClick={openManageDataPage} tabIndex={0}>
                  portfolio policy
                </LinkStyle>
              )}
            </span>
          </FooterStatusRevamped>

          <>
            <ResetButton
              className="qa-revert-constraints"
              disabled={!isInEditState || hasNoChanges}
              onClick={onConstraintsRevert}
              $portfolioLabFF={hasPortfolioLabFF}
            >
              Revert
            </ResetButton>

            <ActionButton
              className="qa-save-constraints"
              disabled={!isInEditState || hasConstraintsError}
              dominant
              onClick={onConstraintsSave}
              $portfolioLabFF={hasPortfolioLabFF}
            >
              Save
            </ActionButton>
          </>
        </Footer>
        {confirmPolicyOverride && (
          <ConfirmationModal
            header="Replace existing policy?"
            subhead="Your current policy will be overridden. This action cannot be undone."
            className="update-constraints-modal"
            onCancel={() => {
              setConfirmPolicyOverride(false);
            }}
            onProceed={() => {
              onApplyToPolicy();
              setConfirmPolicyOverride(false);
            }}
            proceedLabel="Continue"
          />
        )}
      </>
    );
  }

  return (
    <>
      <Footer>
        <FooterStatus>
          Constraints {matchesPolicy ? 'match ' : 'do not match '}{' '}
          <LinkStyle onClick={openManageDataPage} tabIndex={0}>
            portfolio policy
          </LinkStyle>
        </FooterStatus>
        {!matchesPolicy ? (
          <>
            <Tooltip isHidden={isSideBarMaximized} content="Reset constraints">
              <FooterButton onClick={onResetToPolicyConstraints}>
                {isSideBarMaximized && 'Reset constraints'} <Icon type="sync" prefix="far" />
              </FooterButton>
            </Tooltip>
            <Tooltip isHidden={isSideBarMaximized} content="Save as portfolio policy">
              <FooterButton
                onClick={() => {
                  setConfirmPolicyOverride(true);
                }}
              >
                {isSideBarMaximized && 'Save as portfolio policy'} <Icon type="sign-out" prefix="far" />
              </FooterButton>
            </Tooltip>
          </>
        ) : (
          <FooterStatus>
            <HighlightIcon type="check-circle" prefix="far" />
          </FooterStatus>
        )}
      </Footer>
      {confirmPolicyOverride && (
        <ConfirmationModal
          header="Replace existing policy?"
          subhead="Your current policy will be overridden. This action cannot be undone."
          className="update-constraints-modal"
          onCancel={() => {
            setConfirmPolicyOverride(false);
          }}
          onProceed={() => {
            onApplyToPolicy();
            setConfirmPolicyOverride(false);
          }}
          proceedLabel="Continue"
        />
      )}
    </>
  );
};

export default ConstraintsPolicyStatusFooter;

const Footer = styled.div<{ hasConstraintsRevamp?: boolean; $portfolioLabFF?: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: ${({ hasConstraintsRevamp }) => (hasConstraintsRevamp ? '50' : '26')}px;
  width: 100%;
  border-top: 1px solid ${GetColor.GreyScale.Grey40};
  background-color: ${GetColor.White};
  color: ${({ hasConstraintsRevamp }) => (hasConstraintsRevamp ? '' : GetColor.GreyScale.Grey80)};
  display: flex;
`;

const FooterStatus = styled.div`
  height: 100%;
  padding: 0 10px;
  display: flex;
  align-items: center;
  background-color: ${GetColor.GreyScale.Grey10};
  border-right: 1px solid ${GetColor.GreyScale.Grey40};
  & > :last-child {
    margin-left: 3px;
  }
`;

const Container = styled.div`
  position: absolute;
  bottom: 0px;
  left: 0px;
`;

const PolicySync = styled(ButtonIcon)<{
  matchesPolicy: boolean;
  isInEditState: boolean;
  $disabled?: boolean;
  $portfolioLabFF?: boolean;
}>`
  width: ${({ $portfolioLabFF }) => ($portfolioLabFF ? 56 : 50)}px;
  height: ${({ $portfolioLabFF }) => ($portfolioLabFF ? 48 : 49)}px;
  background-color: ${GetColor.GreyScale.Grey10};

  & i {
    color: ${({ matchesPolicy, isInEditState, $disabled }) =>
      $disabled || isInEditState
        ? GetColor.GreyScale.Grey50
        : !matchesPolicy
          ? GetColor.Primary.Dark
          : GetColor.GreyScale.Grey90};
    font-size: 20px;
  }
`;

const StyledIcon = styled(Icon)<{ matchesPolicy: boolean }>`
  position: absolute;
  bottom: 18px;
  left: 38px;
  color: ${({ matchesPolicy }) => (!matchesPolicy ? GetColor.Primary.Dark : GetColor.GreyScale.Grey90)};
`;

const StyledSimpleMenu = styled(SimpleMenu)`
  & span:hover span {
    color: ${GetColor.Primary.Dark};
  }
` as unknown as typeof SimpleMenu;

const MatchesPolicyIcon = styled(Icon)<{ matchesPolicy: boolean }>`
  color: ${({ matchesPolicy }) => (!matchesPolicy ? GetColor.Error : GetColor.HighlightDark)};
`;

const FooterStatusRevamped = styled.div<{ isInEditState: boolean; $portfolioLabFF?: boolean }>`
  max-width: 250px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: ${({ isInEditState }) => (isInEditState ? GetColor.GreyScale.Grey20 : GetColor.GreyScale.Grey10)};
  color: ${({ isInEditState }) => (isInEditState ? GetColor.GreyScale.Grey50 : 'initial')};
  border-left: 1px solid ${GetColor.GreyScale.Grey40};
  border-right: 1px solid ${GetColor.GreyScale.Grey40};
  font-size: 1.125rem;
  flex-grow: 1;
  padding: 0 10px;
  margin-left: ${({ $portfolioLabFF }) => ($portfolioLabFF ? 56 : 50)}px;
  cursor: ${({ isInEditState }) => (isInEditState ? 'not-allowed' : 'initial')};
`;

const StatusHeader = styled.span`
  font-weight: bold;

  & i {
    margin-left: 3px;
  }
`;

const ActionButton = styled(Button)<{ $portfolioLabFF?: boolean }>`
  margin-right: 0;
  height: ${({ $portfolioLabFF }) => ($portfolioLabFF ? '100%' : '50px')};
  border-radius: 0;
  flex-grow: 1;
  ${({ $portfolioLabFF }) =>
    $portfolioLabFF &&
    css`
      border-top: none;
      border-right: none;
      border-bottom: none;
    `}

  &:disabled {
    background-color: transparent;
    color: ${GetColor.GreyScale.Grey60};
    border-top: none;
    ${({ $portfolioLabFF }) =>
      $portfolioLabFF &&
      css`
        border-right: none;
        border-bottom: none;
      `}
  }

  &:focus {
    border: 1px solid ${GetColor.HighlightDark};
  }
`;

const ResetButton = styled(ActionButton)<{ $portfolioLabFF?: boolean }>`
  border: none;
  border-bottom: 1px solid ${GetColor.GreyScale.Grey40};

  &:hover {
    border: none !important;
    border-bottom: 1px solid ${GetColor.GreyScale.Grey40} !important;
  }

  ${({ $portfolioLabFF }) =>
    $portfolioLabFF &&
    css`
      border-bottom: none;

      &:hover {
        border-bottom: none !important;
      }
    `}
`;

const FooterButton = styled(Button)`
  margin-right: 0;
  border: none;
  border-radius: 0;
  border-right: 1px solid ${GetColor.GreyScale.Grey40};
  min-height: 25px;
  height: 25px;
  min-width: 26px;
  padding: 0 10px;
  &:hover {
    border-right: 1px solid ${GetColor.GreyScale.Grey40};
  }
  &:focus {
    border: 1px solid ${GetColor.HighlightDark};
  }
`;

const HighlightIcon = styled(Icon)`
  color: ${GetColor.HighlightDark};
`;
